import { Sidebar } from '../modules/Sidebar';

class Search {
    constructor() {
        this.root = document.querySelector('[data-search-overlay]');
        this.panel = document.querySelector('[js-menu-element="search"]');
        this.nav = document.querySelector('[js-nav]');
        this.megamenuWrapper = document.querySelector('[js-megamenu-wrapper]');

        if (
            !this.root ||
            !this.panel ||
            typeof window === 'undefined' ||
            !window.mdaSearch
        ) {
            return;
        }

        this.input = this.root.querySelector('[data-search-input]');
        this.columnsRoot = this.root.querySelector('[data-search-columns]');
        this.allResultsWrap = this.root.querySelector(
            '[data-search-all-results-wrap]'
        );
        this.allResultsLink = this.root.querySelector(
            '[data-search-all-results]'
        );
        this.closeButtons = document.querySelectorAll('[js-search-close]');
        this.overlay = document.querySelector('[js-overlay]');
        this.searchTrigger = document.querySelector(
            '[js-trigger-menu-element="search"]'
        );
        this.defaults = window.mdaSearch.defaults || {};
        this.searchResultsUrl = String(
            window.mdaSearch.searchResultsUrl || window.location.origin + '/'
        );
        this.phraseQueryKey = String(window.mdaSearch.phraseQueryKey || 's');
        this.allResultsLabel = String(
            window.mdaSearch.allResultsLabel || 'Zobacz wszystkie wyniki'
        );
        this.resultsLimit = Number(window.mdaSearch.resultsLimit) || 4;
        this.productsLimit = Number(window.mdaSearch.productsLimit) || 11;
        this.resultsHeading = String(
            window.mdaSearch.resultsHeading || 'Wyniki wyszukiwania'
        );
        this.productsHeading = String(
            window.mdaSearch.productsHeading || 'Produkty'
        );
        this.categoriesHeading = String(
            window.mdaSearch.categoriesHeading || 'Kategorie'
        );
        this.collectionsHeading = String(
            window.mdaSearch.collectionsHeading || 'Kolekcje'
        );
        this.elasticApiUrl = String(window.mdaSearch.elasticApiUrl || '');
        this.elasticApiToken = String(window.mdaSearch.elasticApiToken || '');
        this.placeholderImageUrl = String(
            window.mdaSearch.placeholderImageUrl || ''
        );
        this.allResultsEllipseUrl = String(
            window.mdaSearch.allResultsEllipseUrl || ''
        );
        this.language = String(window.mdaSearch.language || '');
        this.configuratorLinkLabel = String(
            window.mdaSearch.configuratorLinkLabel || 'Przejdź do konfiguratora'
        );
        this.debounceMs = Number(window.mdaSearch.debounceMs) || 280;
        this.noResultsLabel = String(
            window.mdaSearch.noResultsLabel || 'Brak wyników'
        );
        this.scrollContainer = this.root.querySelector('[data-search-scroll]');
        this.emptyState = this.root.querySelector('[data-search-empty]');
        this.clearButton = this.root.querySelector('[data-search-clear]');
        this.debounceTimer = null;
        this.searchRequestId = 0;
        this.isOpen = false;

        this.sectionKeys = [
            'suggestions',
            'categories',
            'collections',
            'products',
        ];
        this.sections = this.sectionKeys.reduce((acc, key) => {
            acc[key] = {
                headings: this.root.querySelectorAll(
                    `[data-search-section-heading="${key}"]`
                ),
                items: this.root.querySelectorAll(
                    `[data-search-section-items="${key}"]`
                ),
                roots: this.root.querySelectorAll(
                    `[data-search-section="${key}"]`
                ),
            };

            return acc;
        }, {});

        this.init();
    }

    init() {
        this.render(this.defaults);
        this.bindEvents();
        this.observePanel();
    }

    bindEvents() {
        this.input?.addEventListener('input', this.onInput.bind(this));
        this.input?.addEventListener('keydown', this.onInputKeydown.bind(this));
        this.clearButton?.addEventListener('click', this.onClear.bind(this));

        this.root.addEventListener('click', this.onRootClick.bind(this));

        this.closeButtons.forEach(button => {
            button.addEventListener('click', this.close.bind(this));
        });

        document.addEventListener('keydown', this.onDocumentKeydown.bind(this));
    }

    observePanel() {
        const observer = new MutationObserver(() => {
            const isActive = this.panel.classList.contains('-active');

            if (isActive === this.isOpen) {
                return;
            }

            this.isOpen = isActive;
            this.nav?.classList.toggle('search-open', isActive);
            this.megamenuWrapper?.classList.toggle('-search-active', isActive);

            if (isActive) {
                Sidebar.lockPageScroll();
                window.setTimeout(() => {
                    this.input?.focus();
                }, 120);
                this.scheduleSearchFetch(this.getQuery());
                return;
            }

            Sidebar.unlockPageScroll();

            if (this.input) {
                this.input.value = '';
            }

            this.updateClearButton();
            this.scrollContainer?.scrollTo({ top: 0 });
            this.render(this.defaults);
            this.renderProducts([]);
            this.renderEmptyState(false);
        });

        observer.observe(this.panel, {
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    onInput() {
        this.updateClearButton();
        this.scheduleSearchFetch(this.getQuery());
    }

    onClear() {
        if (!this.input) {
            return;
        }

        this.input.value = '';
        this.updateClearButton();
        this.scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
        this.render(this.defaults);
        this.renderProducts([]);
        this.renderEmptyState(false);
        this.input.focus();
    }

    updateClearButton() {
        if (!this.clearButton) {
            return;
        }

        const hasQuery = this.getQuery() !== '';

        this.clearButton.classList.toggle('hidden', !hasQuery);
    }

    onInputKeydown(event) {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();
        this.submitQuery(this.getQuery());
    }

    onDocumentKeydown(event) {
        if (event.key === 'Escape' && this.isOpen) {
            event.preventDefault();
            this.close();
        }
    }

    onRootClick(event) {
        const suggestionButton = event.target.closest(
            '[data-search-suggestion]'
        );

        if (!suggestionButton) {
            return;
        }

        event.preventDefault();

        const phrase =
            suggestionButton.getAttribute('data-search-suggestion') || '';

        if (phrase === '') {
            return;
        }

        const action =
            suggestionButton.getAttribute('data-search-suggestion-action') ||
            'fill';

        if (action === 'submit') {
            this.submitQuery(phrase);
            return;
        }

        this.fillSuggestion(phrase);
    }

    fillSuggestion(phrase) {
        if (!this.input) {
            return;
        }

        const normalizedPhrase = String(phrase || '').trim();

        if (normalizedPhrase === '') {
            return;
        }

        this.input.value = normalizedPhrase;
        this.updateClearButton();
        this.scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
        this.scheduleSearchFetch(normalizedPhrase);
        this.input.focus();
    }

    close() {
        if (!this.isOpen) {
            return;
        }

        if (this.searchTrigger?.classList.contains('active')) {
            this.searchTrigger.click();
            return;
        }

        this.overlay?.click();
    }

    getQuery() {
        return String(this.input?.value || '').trim();
    }

    matchesQuery(values, normalizedQuery) {
        return values.some(value => {
            const normalizedValue = String(value || '')
                .trim()
                .toLowerCase();

            return (
                normalizedValue !== '' &&
                normalizedValue.includes(normalizedQuery)
            );
        });
    }

    getStaticArchiveItems(sectionKey, phrase = '') {
        const items = this.defaults?.sections?.[sectionKey]?.items || [];
        const normalizedQuery = String(phrase || '')
            .trim()
            .toLowerCase();

        return items.filter(item => {
            if (!item?.isArchive) {
                return false;
            }

            if (normalizedQuery === '') {
                return true;
            }

            if (sectionKey === 'categories') {
                return this.matchesQuery(
                    [item.name, item.parentName, item.slug],
                    normalizedQuery
                );
            }

            return this.matchesQuery([item.name, item.slug], normalizedQuery);
        });
    }

    mergeSectionItems(sectionKey, dynamicItems = [], phrase = '') {
        const staticItems = this.getStaticArchiveItems(sectionKey, phrase);
        const dynamicLimit = Math.max(
            0,
            this.resultsLimit - staticItems.length
        );

        return [
            ...staticItems,
            ...(Array.isArray(dynamicItems)
                ? dynamicItems.slice(0, dynamicLimit)
                : []),
        ];
    }

    mergeProductItems(dynamicItems = [], phrase = '') {
        const dynamic = Array.isArray(dynamicItems)
            ? dynamicItems.slice(0, this.productsLimit)
            : [];
        const normalizedPhrase = String(phrase || '').trim();

        if (
            normalizedPhrase === '' ||
            this.allResultsLabel === '' ||
            dynamic.length === 0
        ) {
            return dynamic;
        }

        return [...dynamic, { isAllResults: true }];
    }

    submitQuery(query) {
        const phrase = String(query || '').trim();

        if (phrase === '') {
            return;
        }

        window.location.href = this.getSearchResultsUrl(phrase);
    }

    getSearchResultsUrl(phrase) {
        const url = new URL(this.searchResultsUrl, window.location.origin);

        url.searchParams.set(this.phraseQueryKey, phrase);

        return url.toString();
    }

    scheduleSearchFetch(query = '') {
        window.clearTimeout(this.debounceTimer);

        const phrase = String(query || '').trim();

        if (phrase === '') {
            this.render(this.defaults);
            this.renderProducts([]);
            this.renderEmptyState(false);
            return;
        }

        this.debounceTimer = window.setTimeout(() => {
            this.fetchSearchResults(phrase);
        }, this.debounceMs);
    }

    buildElasticRequestBody(phrase) {
        return {
            listing: 'searchOverlay',
            phrase,
            language: this.language,
            resultsHeading: this.resultsHeading,
            productsHeading: this.productsHeading,
            categoriesHeading: this.categoriesHeading,
            collectionsHeading: this.collectionsHeading,
            limits: {
                categories: this.resultsLimit,
                collections: this.resultsLimit,
                products: this.productsLimit,
            },
        };
    }

    async fetchSearchResults(phrase) {
        if (this.elasticApiUrl === '') {
            this.renderProducts([]);
            return;
        }

        const requestId = ++this.searchRequestId;

        try {
            const headers = {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            };

            if (this.elasticApiToken !== '') {
                headers['X-Listing-Token'] = this.elasticApiToken;
            }

            const response = await fetch(this.elasticApiUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify(this.buildElasticRequestBody(phrase)),
            });

            if (!response.ok || requestId !== this.searchRequestId) {
                return;
            }

            const data = await response.json();

            if (!data?.success || requestId !== this.searchRequestId) {
                return;
            }

            const sections = data.sections || {};
            const categoryItems = this.mergeSectionItems(
                'categories',
                sections.categories?.items || [],
                phrase
            );
            const collectionItems = this.mergeSectionItems(
                'collections',
                sections.collections?.items || [],
                phrase
            );
            const productItems = Array.isArray(sections.products?.items)
                ? sections.products.items
                : [];
            const suggestionItems = Array.isArray(sections.suggestions?.items)
                ? sections.suggestions.items
                : [];
            const hasResults =
                suggestionItems.length > 0 ||
                categoryItems.length > 0 ||
                collectionItems.length > 0 ||
                productItems.length > 0;

            this.render({
                sections: {
                    suggestions: {
                        heading:
                            sections.suggestions?.heading ||
                            this.resultsHeading,
                        items: suggestionItems,
                    },
                    categories: {
                        heading:
                            sections.categories?.heading ||
                            this.categoriesHeading,
                        items: categoryItems,
                    },
                    collections: {
                        heading:
                            sections.collections?.heading ||
                            this.collectionsHeading,
                        items: collectionItems,
                    },
                },
            });
            this.renderProducts(this.mergeProductItems(productItems, phrase));
            this.renderEmptyState(!hasResults);
        } catch (error) {
            if (requestId === this.searchRequestId) {
                this.renderProducts([]);
                this.renderEmptyState(this.getQuery() !== '');
            }
        }
    }

    renderEmptyState(show = false) {
        if (!this.emptyState) {
            return;
        }

        const shouldShow = Boolean(show) && this.getQuery() !== '';

        this.emptyState.textContent = shouldShow ? this.noResultsLabel : '';
        this.emptyState.classList.toggle('hidden', !shouldShow);
        this.columnsRoot?.classList.toggle('hidden', shouldShow);
    }

    render(data = {}) {
        const sections = data.sections || {};
        const query = this.getQuery();
        const isSearching = query !== '';
        let hasAnySection = false;

        this.sectionKeys.forEach(key => {
            if (key === 'products') {
                return;
            }

            const section = sections[key] || {};
            const config = this.sections[key];

            if (!config) {
                return;
            }

            let heading = String(section.heading || '').trim();

            if (isSearching && key === 'suggestions') {
                heading = this.resultsHeading;
            }

            const items = Array.isArray(section.items)
                ? section.items.slice(0, this.resultsLimit)
                : [];
            const itemsHtml = items
                .map(item => this.renderItem(key, item, isSearching))
                .join('');

            config.headings.forEach(headingElement => {
                headingElement.textContent = heading;
                headingElement.classList.toggle('hidden', heading === '');
            });

            config.items.forEach(itemsElement => {
                itemsElement.innerHTML = itemsHtml;
            });

            config.roots.forEach(sectionRoot => {
                sectionRoot.classList.toggle('hidden', items.length === 0);
            });

            if (items.length > 0) {
                hasAnySection = true;
            }
        });

        this.columnsRoot?.classList.toggle(
            'hidden',
            !hasAnySection && !isSearching
        );
        this.renderAllResultsCta(isSearching, query);
    }

    renderProducts(items = []) {
        const config = this.sections.products;

        if (!config) {
            return;
        }

        const products = Array.isArray(items) ? items : [];
        const phrase = this.getQuery();
        const heading = this.productsHeading;
        const itemsHtml = products
            .map(item =>
                item?.isAllResults
                    ? this.renderAllResultsGridCard(phrase)
                    : this.renderProductCard(item)
            )
            .join('');

        config.headings.forEach(headingElement => {
            headingElement.textContent = heading;
            headingElement.classList.toggle('hidden', heading === '');
        });

        config.items.forEach(itemsElement => {
            itemsElement.innerHTML = itemsHtml;
        });

        config.roots.forEach(sectionRoot => {
            sectionRoot.classList.toggle('hidden', products.length === 0);
            sectionRoot.classList.toggle('flex', products.length > 0);
        });

        if (
            products.length > 0 &&
            this.columnsRoot?.classList.contains('hidden')
        ) {
            this.columnsRoot.classList.remove('hidden');
        }
    }

    getPlaceholderImageUrl() {
        return String(
            this.placeholderImageUrl ||
                this.root?.dataset?.placeholderImageUrl ||
                window.mdaSearch?.placeholderImageUrl ||
                ''
        ).trim();
    }

    resolveImageSrc(primaryUrl = '') {
        const normalizedPrimary = String(primaryUrl || '').trim();

        if (normalizedPrimary !== '') {
            return normalizedPrimary;
        }

        return this.getPlaceholderImageUrl();
    }

    renderProductCard(item) {
        const url = this.escapeHtml(item.url || '#');
        const title = this.escapeHtml(item.title || '');
        const meta = this.escapeHtml(item.meta || '');
        const price = this.escapeHtml(item.price || '');
        const badge = this.escapeHtml(item.badge || '');
        const thumbnails = Array.isArray(item.thumbnails)
            ? item.thumbnails
            : [];
        const imageAlt = this.escapeHtml(
            item.image?.alt || thumbnails[0]?.alt || title
        );
        const imageSrc = this.escapeHtml(
            this.resolveImageSrc(item.image?.url || thumbnails[0]?.url || '')
        );

        if (title === '') {
            return '';
        }

        const imageHtml =
            imageSrc !== ''
                ? `<img class="h-full w-full object-cover" src="${imageSrc}" alt="${imageAlt}" loading="lazy" decoding="async" width="246" height="246">`
                : '';

        const badgeHtml =
            badge !== ''
                ? `
                    <div class="search-product-card__badge absolute left-12 top-12">
                        <div class="bg-pink-100 border-pink-100 text-grey-900 text-tag-font-text rounded-full h-24 min-h-[24px] flex items-center justify-center p-8 font-medium flex border-tiny">
                            <span>${badge}</span>
                        </div>
                    </div>
                `
                : '';

        return `
            <a
                href="${url}"
                class="search-product-card group"
                title="${title}"
            >
                <div class="search-product-card__media relative bg-product-background">
                    ${imageHtml}
                    ${badgeHtml}
                </div>
                <div class="search-product-card__body flex flex-col gap-4 pt-12">
                    ${
                        meta !== ''
                            ? `<p class="search-product-card__meta m-0 truncate">${meta}</p>`
                            : ''
                    }
                    <p class="search-product-card__title m-0">${title}</p>
                    ${
                        price !== ''
                            ? `<p class="search-product-card__price m-0 mt-auto">${price}</p>`
                            : ''
                    }
                </div>
            </a>
        `;
    }

    renderAllResultsGridCard(phrase = '') {
        const normalizedPhrase = String(phrase || '').trim();
        const label = this.escapeHtml(this.allResultsLabel);

        if (normalizedPhrase === '' || label === '') {
            return '';
        }

        const url = this.escapeHtml(this.getSearchResultsUrl(normalizedPhrase));
        const ellipseUrl = this.escapeHtml(this.allResultsEllipseUrl);
        const ellipseHtml =
            ellipseUrl !== ''
                ? `<img class="search-product-card__all-results-ellipse" src="${ellipseUrl}" alt="" decoding="async">`
                : '';

        return `
            <a
                href="${url}"
                class="search-product-card search-product-card--all-results group"
                title="${label}"
            >
                ${ellipseHtml}
                <span class="search-product-card__all-results-label">${label}</span>
                <i class="icon icon-arrow_outward search-product-card__all-results-icon" aria-hidden="true"></i>
            </a>
        `;
    }

    renderAllResultsCta(isSearching, query) {
        if (!this.allResultsWrap || !this.allResultsLink) {
            return;
        }

        this.allResultsWrap.classList.add('hidden');
        this.allResultsLink.removeAttribute('href');
        this.allResultsLink.innerHTML = '';
    }

    dedupePreviewItems(previews = []) {
        const seen = new Set();

        return (Array.isArray(previews) ? previews : []).filter(entry => {
            const url = String(entry?.url || '').trim();

            if (url === '' || seen.has(url)) {
                return false;
            }

            seen.add(url);

            return true;
        });
    }

    resolveSectionPreviews(item = {}) {
        if (Array.isArray(item.previews) && item.previews.length > 0) {
            return this.dedupePreviewItems(item.previews);
        }

        if (String(item.image?.url || '').trim() !== '') {
            return [
                {
                    url: item.image.url,
                    alt: item.image.alt || item.name || '',
                },
            ];
        }

        return [];
    }

    renderMenuStackedImages(previews = [], fallbackName = '') {
        const items = this.dedupePreviewItems(previews).slice(0, 4);

        if (items.length === 0) {
            return '<div class="flex items-center h-[40px] desktop:h-[48px]" aria-hidden="true"></div>';
        }

        if (items.length === 1) {
            const preview = items[0];

            return `
                <span class="inline-flex w-[40px] h-[40px] desktop:w-[48px] desktop:h-[48px] rounded-full overflow-hidden bg-beige-100 shrink-0">
                    <img
                        class="h-full w-full object-cover"
                        src="${this.escapeHtml(preview.url)}"
                        alt="${this.escapeHtml(preview.alt || fallbackName)}"
                        loading="lazy"
                        decoding="async"
                        width="48"
                        height="48"
                    >
                </span>
            `;
        }

        const imagesHtml = items
            .map((preview, index) => {
                const isLast = index === items.length - 1;
                const overlapClass = isLast ? '' : ' -mr-[24px]';

                return `
                    <img
                        class="w-[40px] desktop:w-[48px] h-[40px] desktop:h-[48px] rounded-full border-[2px] border-background-primary object-cover bg-beige-100${overlapClass}"
                        src="${this.escapeHtml(preview.url)}"
                        alt="${this.escapeHtml(preview.alt || fallbackName)}"
                        loading="lazy"
                        decoding="async"
                        width="48"
                        height="48"
                    >
                `;
            })
            .join('');

        return `
            <div class="flex items-center h-[40px] desktop:h-[48px] bg-background-primary">
                ${imagesHtml}
            </div>
        `;
    }

    renderItem(sectionKey, item, isSearching = false) {
        if (sectionKey === 'suggestions') {
            return this.renderSuggestionItem(item, isSearching);
        }

        if (sectionKey === 'categories') {
            return this.renderCategoryItem(item);
        }

        return this.renderCollectionItem(item);
    }

    renderSuggestionItem(item, isSearching = false) {
        const label = this.escapeHtml(item.label || '');
        const phrase = this.escapeHtml(item.phrase || item.label || '');

        if (label === '') {
            return '';
        }

        const iconClass = isSearching
            ? 'icon-search_check text-text-accent'
            : 'icon-lightbulb text-text-secondary';

        return `
            <button
                type="button"
                class="search-overlay__suggestion group flex items-center gap-16 h-[57px] border-t border-border-primary text-left cursor-pointer w-full"
                data-search-suggestion="${phrase}"
                data-search-suggestion-action="${
                    isSearching ? 'submit' : 'fill'
                }"
            >
                <i class="icon ${iconClass} shrink-0" aria-hidden="true"></i>
                <span class="search-overlay__item-title group-hover:text-text-accent transition-colors">${label}</span>
            </button>
        `;
    }

    renderCategoryItem(item) {
        const url = this.escapeHtml(item.url || '#');
        const name = this.escapeHtml(item.name || '');
        const parentName = this.escapeHtml(item.parentName || '');
        const configuratorLink = this.escapeHtml(item.configuratorLink || '');
        const previews = this.resolveSectionPreviews(item);
        const previewHtml = this.renderMenuStackedImages(previews, name);

        if (name === '') {
            return '';
        }

        const configuratorHtml =
            configuratorLink !== ''
                ? `
                    <a
                        class="shrink-0"
                        href="${configuratorLink}"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="${this.escapeHtml(
                            this.configuratorLinkLabel
                        )}"
                    >
                        <i class="icon icon-tune tune-icon" aria-hidden="true"></i>
                    </a>
                `
                : '';

        return `
            <div class="flex items-center justify-between gap-16 py-4 border-t border-border-primary group">
                <div class="flex flex-col gap-4 min-w-0 pr-12">
                    <span class="flex items-center gap-8 min-w-0">
                        <a
                            href="${url}"
                            class="search-overlay__item-title group-hover:text-text-accent transition-colors min-w-0"
                        >${name}</a>
                        ${configuratorHtml}
                    </span>
                    ${
                        parentName
                            ? `<span class="search-overlay__item-subtitle">${parentName}</span>`
                            : ''
                    }
                </div>
                <a href="${url}" class="shrink-0">${previewHtml}</a>
            </div>
        `;
    }

    renderCollectionItem(item) {
        const url = this.escapeHtml(item.url || '#');
        const name = this.escapeHtml(item.name || '');
        const imageUrl = String(item.image?.url || '').trim();
        const imageAlt = this.escapeHtml(item.image?.alt || name);

        if (name === '') {
            return '';
        }

        const previewHtml =
            imageUrl !== ''
                ? `
                    <span class="inline-flex w-[53px] h-[40px] desktop:w-[64px] desktop:h-[48px] rounded-small overflow-hidden bg-beige-100 shrink-0">
                        <img
                            class="w-full h-full object-cover"
                            src="${this.escapeHtml(imageUrl)}"
                            alt="${imageAlt}"
                            loading="lazy"
                            decoding="async"
                            width="97"
                            height="48"
                        >
                    </span>
                `
                : `
                    <span class="inline-flex w-[97px] h-48 rounded-small bg-beige-100 shrink-0" aria-hidden="true"></span>
                `;

        return `
            <a
                href="${url}"
                class="flex items-center justify-between gap-16 py-4 border-t border-border-primary group"
            >
                <span class="search-overlay__item-title group-hover:text-text-accent transition-colors">${name}</span>
                ${previewHtml}
            </a>
        `;
    }

    escapeHtml(value) {
        return String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }
}

const initSearch = () => {
    new Search();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearch);
} else {
    initSearch();
}
